import React, { useState } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, Mail, Phone, Calendar, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';

export const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, member, emailVerified } = useAuth();

  const [name, setName] = useState(member?.name || '');
  const [phone, setPhone] = useState(member?.phone || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (name.trim().length < 2) {
      setMessage({ type: 'error', text: 'Name must be at least 2 characters.' });
      return;
    }

    if (phone.trim().length < 6) {
      setMessage({ type: 'error', text: 'Please enter a valid phone number.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      // Update profile in Firestore (strictly limited to permitted fields by Security Rules)
      const memberRef = doc(db, 'members', user.uid);
      await setDoc(memberRef, {
        name: name.trim(),
        phone: phone.trim(),
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setMessage({ type: 'success', text: t('profile.success') });
    } catch (err: any) {
      console.error('[FHPF] Profile update error:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user) return;
    setMessage(null);
    try {
      await sendEmailVerification(user, {
        url: window.location.origin + '/profile',
        handleCodeInApp: true,
      });
      setVerificationSent(true);
      setMessage({
        type: 'success',
        text: 'Verification link sent! Please check your Inbox and Spam/Junk folder.',
      });
    } catch (err: any) {
      console.error('[FHPF] Verification email error:', err);
      if (err.code === 'auth/unauthorized-domain') {
        setMessage({
          type: 'error',
          text: `Domain ${window.location.hostname} must be added in Firebase Console > Authentication > Settings > Authorized domains.`,
        });
      } else if (err.code === 'auth/too-many-requests') {
        setMessage({
          type: 'error',
          text: 'Too many requests. Please wait a few minutes before requesting another verification email.',
        });
      } else {
        setMessage({ type: 'error', text: err.message || 'Failed to send verification email.' });
      }
    }
  };

  const handleRefreshStatus = async () => {
    if (!user) return;
    try {
      await user.reload();
      if (user.emailVerified) {
        window.location.reload();
      } else {
        setMessage({
          type: 'error',
          text: 'Email not verified yet. Please click the link in your email first (check Spam/Junk folder).',
        });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Failed to refresh status.' });
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-4 sm:py-8 pb-24 sm:pb-8 space-y-6">
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm p-6 sm:p-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
          <User className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          <span>{t('profile.title')}</span>
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-6">
          Manage your personal details and account verification.
        </p>

        {/* Notifications */}
        {message && (
          <div
            className={`mb-5 p-3.5 rounded-xl border flex items-start gap-2 text-xs sm:text-sm ${
              message.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
                : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0 text-red-600 mt-0.5" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Email Verification Box */}
        <div className="mb-6 p-4 rounded-xl bg-gray-50 dark:bg-navy-950/40 border border-gray-200 dark:border-navy-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-0.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Email Status
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
              <span>{user?.email}</span>
              {emailVerified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                  <CheckCircle2 className="w-3 h-3" />
                  Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                  <AlertCircle className="w-3 h-3" />
                  Unverified
                </span>
              )}
            </div>
          </div>

          {!emailVerified && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={verificationSent}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium text-xs transition-colors shrink-0 disabled:opacity-60"
              >
                {verificationSent ? t('auth.verificationSent') : t('auth.resendVerification')}
              </button>
              <button
                type="button"
                onClick={handleRefreshStatus}
                className="px-3 py-1.5 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-navy-800 dark:hover:bg-navy-700 text-gray-800 dark:text-gray-200 font-medium text-xs transition-colors shrink-0"
              >
                Check Status
              </button>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1.5">
              {t('profile.legalName')}
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3.5 py-2.5 text-sm bg-gray-50 dark:bg-navy-950/60 border border-gray-300 dark:border-navy-700 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-1.5">
              {t('profile.phone')}
            </label>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+8801XXXXXXXXX"
              className="w-full px-3.5 py-2.5 text-sm bg-gray-50 dark:bg-navy-950/60 border border-gray-300 dark:border-navy-700 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none text-gray-900 dark:text-white"
            />
          </div>

          {/* Readonly Metadata */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="p-3 rounded-xl bg-gray-50/70 dark:bg-navy-950/30 border border-gray-200/60 dark:border-navy-800">
              <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {t('profile.joinDate')}
              </span>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {member?.joinDate || 'N/A'}
              </span>
            </div>
            <div className="p-3 rounded-xl bg-gray-50/70 dark:bg-navy-950/30 border border-gray-200/60 dark:border-navy-800">
              <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {t('profile.membershipStatus')}
              </span>
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                {member?.status?.toUpperCase() || 'ACTIVE'}
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm shadow-md shadow-primary-600/20 transition-all disabled:opacity-60 mt-4"
          >
            {saving ? t('profile.saving') : t('profile.saveChanges')}
          </button>
        </form>
      </div>
    </div>
  );
};
